
// src/hooks/userManagement.js
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { recordError } from '../utils/logService';

import {
  Messages,
  Notify,
  openNotification,
  usersService,
  util,
} from '@seims/shared';

export const usersKeys = {
  all: ['users'],
  list: (params) => ['users', 'list', params],
  roles: (serviceType) => ['users', 'roles', serviceType],
  email: (email) => ['users', 'email', email],
};

export const mapUserRow = (e) => ({
  key: `${e.id}X${String(e.id)}`,
  id: e.id,
  emailAddress: e.emailAddress,
  firstname: e.firstname,
  lastname: e.lastname,
  name: `${e.firstname} ${e.lastname}`.trim(),
  loginTimestamp: e.loginTimestamp,
  registrationTimestamp: e.registrationTimestamp,
  enabled: e.enabled,
  roles: e.roles,
  emailVerified: e.emailVerified,
});

/**
 * Get roles for a service type and optionally add All Roles at the front.
 * Returns: Array<{key:string, value:string}>
 */
export function useRoles(serviceType, { includeAllRoles, allRoles }) {
  return useQuery({
    queryKey: usersKeys.roles(serviceType),
    queryFn: async () => {
      const res = await usersService.getRoles(serviceType);
      const roles = Array.isArray(res?.data) ? res.data : [];
      if (includeAllRoles && allRoles) {
        const all = { key: '-', value: allRoles };
        return [all, ...roles];
      }
      return roles;
    },
    enabled: !!serviceType,
    staleTime: 120_000,
  });
}

export function useUsersList(params) {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: async () => {
      const res = await usersService.getUsers(params);
      const { data } = res || {};
      return {
        total: data?.total ?? 0,
        results: Array.isArray(data?.results) ? data.results.map(mapUserRow) : [],
      };
    },
    keepPreviousData: true,
    staleTime: 30_000,
  });
}

export function useEmailExists() {
  const qc = useQueryClient();
  const fetch = async (email) => {
    try {
      const response = await usersService.checkEmailExists(email);
      return response?.status; // 209 ok, 409 handled by caller
    } catch (error) {
      recordError(error);
      throw error;
    }
  };
  const fetchEmailStatus = (email) =>
    qc.fetchQuery({
      queryKey: usersKeys.email(email),
      queryFn: () => fetch(email),
      staleTime: 60_000,
    });
  return { fetchEmailStatus };
}

export function useAddUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersService.addUser,
    onSuccess: () => {
      openNotification(
        'Create user',
        util.parseMessage(Messages.ADD_SUCCESS, 'User'),
        Notify.SUCCESS,
      );
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      recordError(error);
      openNotification(
        'Create user',
        util.parseMessage(Messages.ADD_FAILURE, 'User'),
        Notify.ERROR,
      );
    },
  });
}

export function useUpdateUserDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersService.updateUserDetails,
    onSuccess: () => {
      openNotification(
        'Update user',
        util.parseMessage(Messages.UPDATE_SUCCESS, 'User'),
        Notify.SUCCESS,
      );
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      recordError(error);
      openNotification(
        'Update user',
        util.parseMessage(Messages.UPDATE_FAILURE, 'User'),
        Notify.ERROR,
      );
    },
  });
}

export function useUpdateUserAccess(serviceType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => usersService.updateUserAccess(payload, serviceType),
    onSuccess: () => {
      openNotification(
        'User Access',
        util.parseMessage(Messages.UPDATE_SUCCESS, 'User account access'),
        Notify.SUCCESS,
      );
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      recordError(error);
      openNotification('User Access', 'SYSTEM FAILED TO UPDATE', Notify.ERROR);
    },
  });
}

export const UsersListParamsPropTypes = {
  type: PropTypes.string.isRequired,
  first: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  text: PropTypes.string,
  roleName: PropTypes.string,
};
